import { v4 as uuid } from "uuid";
import {
  limit,
  orderBy,
  query,
  startAfter,
  Transaction,
} from "firebase/firestore";
import {
  toDocumentData,
  getCollectionRef,
  fromDocumentData,
  getDocumentRef,
  updateDocument,
  setDocument,
} from "./FirebaseRepository";
import ContractRowModel from "../models/ContractRowModel";
import { RequiredParamException } from "api/exceptions/InvalidParamException";

class ContractRowRepository {
  private path = (contractUid: string) => `cfContract/${contractUid}/wbs`;

  public async addContractsRowToDataBase(
    data: ContractRowModel,
    transaction?: Transaction
  ): Promise<string> {
    if (!data.contractUid) throw new RequiredParamException("contractUid");

    const uid = uuid();
    const doc = getDocumentRef(
      this.path(data.contractUid),
      uid,
      converter(data.contractUid)
    );

    await setDocument(doc, data, transaction);
    return doc.id;
  }

  public updateContractsRowOnDataBase(
    contractUid: string,
    uid: string,
    updatedData: Partial<ContractRowModel>,
    transaction?: Transaction
  ) {
    if (!contractUid) throw new RequiredParamException("contractUid");
    if (!uid) throw new RequiredParamException("uid");

    const doc = getDocumentRef(this.path(contractUid), uid);
    const data = converter(contractUid).toFirestore(updatedData);
    return updateDocument(doc, data, transaction);
  }

  getContractsRowsQuery(
    contractUid: string,
    startAt?: string,
    limitOf: number = 1000
  ) {
    if (!contractUid) throw new RequiredParamException("contractUid");

    const collection = getCollectionRef(
      this.path(contractUid),
      converter(contractUid)
    );
    if (startAt)
      return query(
        collection,
        orderBy("structured"),
        startAfter(startAt),
        limit(limitOf)
      );

    return query(collection, orderBy("structured"), limit(limitOf));
  }
}

// Firestore data converter
const converter = (contractUid: string) => ({
  toFirestore: (row: Partial<ContractRowModel>) => {
    const data = row instanceof ContractRowModel ? row.toSave() : row;
    return toDocumentData(data);
  },
  fromFirestore: (snapshot, options) => {
    let data = snapshot.data(options);
    data = fromDocumentData(data);
    return ContractRowModel.create(snapshot.id, {
      ...data,
      contractUid,
    });
  },
});

export default new ContractRowRepository();
